/**
 * Route gate that mounts {@link GlobalAuthModal} — and loads its chunk — only on
 * the authenticated app pages it serves, keeping it out of the landing entry.
 *
 * This is the single source of truth for WHERE the in-app auth/upgrade modal
 * intercepts CTAs. See {@link GlobalAuthModal} for why the coverage is limited to
 * these routes (the IDE mounts its own; public pages navigate on purpose).
 *
 * @module
 */

import { Suspense } from 'react'
import { useLocation } from 'react-router'

import { ensureUiProviders } from '../bonds/ui-loader.js'
import { lazyWithReload } from '../lib/chunk-reload.js'

/**
 * Lazy so the auth-modal chunk never lands in the landing entry. `lazyWithReload`
 * because a tab holding a previous deploy's bundle gets `m === undefined` here
 * while it reloads (see lib/chunk-reload.ts).
 */
const GlobalAuthModal = lazyWithReload(() =>
  ensureUiProviders()
    .then(() => import('./GlobalAuthModal.js'))
    .then((m) => ({ default: m.GlobalAuthModal })),
)

/**
 * The authenticated app routes whose Sign up / Log in / Upgrade CTAs (the
 * Dashboard usage banner, the global limit toast) should open the in-app modal
 * rather than navigate. Excludes `/workspace/*` (the IDE mounts its own auth
 * modal — two document interceptors would double-fire) and every public
 * marketing / auth / pricing page (where navigating to the standalone page is the
 * intended behavior).
 */
export const APP_MODAL_PATHS = new Set(['/dashboard', '/analytics', '/plan-updated'])

/**
 * Renders {@link GlobalAuthModal} on a covered route, else nothing.
 *
 * @returns The lazily-loaded modal on an app route, else `null`.
 */
export function GlobalAuthModalGate() {
  const { pathname } = useLocation()
  if (!APP_MODAL_PATHS.has(pathname)) return null
  return (
    <Suspense fallback={null}>
      <GlobalAuthModal />
    </Suspense>
  )
}

GlobalAuthModalGate.displayName = 'GlobalAuthModalGate'
